import type { BuildKind } from './subject-kind';
import { FROM_COST, INTERVAL_COST, OF_PLACEMENT_COST } from './economy';

export type CreationKind = 'of' | 'from' | 'interval';

export interface CreationKindDef {
  kind: CreationKind;
  label: string;
  cost: number;
  /** `from`-only: its fixed total emission count, independent of the source (see `observableCap`). `of` has no fixed length — it only grows via purchased slots, and `interval` has no length at all. */
  sequenceLength?: number;
  /** Absent for `interval`, whose rate is its own player-chosen argument — see `observableRateTicks`. */
  rateTicks?: number;
  /** `interval`-only: the periods on offer in its arg picker, each priced by {@link intervalPeriodCost}. */
  availablePeriodTicks?: number[];
  description: string;
  expandedDescription: string;
  docsUrl: string;
}

export const CREATION_KINDS: Record<CreationKind, CreationKindDef> = {
  of: {
    kind: 'of',
    label: 'of()',
    cost: OF_PLACEMENT_COST,
    rateTicks: 20,
    description: 'starts a stream from a fixed number of packets',
    expandedDescription:
      'A creation operator: it builds an Observable out of the arguments you give it, emits exactly that many packets, and then completes. Free to place, but each slot costs more than the last to add. Buying a slot buys the argument, never the material in it — on a source with a byproduct, each slot is rolled against the source odds at the moment it is emitted, so the same stream can come out differently on the next subscription. Nothing is extracted until you subscribe.',
    docsUrl: 'https://rxjs.dev/api/index/function/of',
  },
  from: {
    kind: 'from',
    label: 'from()',
    cost: FROM_COST,
    sequenceLength: 6,
    rateTicks: 20,
    description: 'starts a stream from an iterable',
    expandedDescription:
      "A creation operator: it builds an Observable out of an iterable. Draws each packet live from the source's odds, one at a time, until it's produced its full count, then completes. Nothing is extracted until you subscribe, and a forgotten subscription keeps draining upkeep even once it's done producing.",
    docsUrl: 'https://rxjs.dev/api/index/function/from',
  },
  interval: {
    kind: 'interval',
    label: 'interval()',
    cost: INTERVAL_COST,
    availablePeriodTicks: [10, 20, 40],
    description: 'emits forever on a timer',
    expandedDescription:
      'A creation operator: it emits on a fixed period and never completes on its own. There is no last packet to wait for and no automatic teardown — the only ways out are `take()` or unsubscribing yourself, so upkeep runs until you stop it. A shorter period costs more, because the stream is endless either way.',
    docsUrl: 'https://rxjs.dev/api/index/function/interval',
  },
};

/** Which half of the toolbar a tool came from: an observable to place on a source, or an operator to slot onto a pipe. */
export function isCreationKind(kind: BuildKind): kind is CreationKind {
  return kind in CREATION_KINDS;
}
